import { Box, FormControl, FormLabel, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { fromError } from '~utils/errors';
import { HOUR_REGEX } from '~utils/timeBlocks';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigBurnInRefreshTime_ConfigFragment,
  useUpdateConfigBurnInRefreshTimeMutation,
} from './__generated__/ConfigBurnInRefreshTime.graphql';

interface Props {
  config: ConfigBurnInRefreshTime_ConfigFragment;
  configStatus?: ConfigurationStatusType;
  onPendingChange: (pending: boolean) => void;
}

export function ConfigBurnInRefreshTime({ config, configStatus, onPendingChange }: Props) {
  const { verifyUserPermissions } = useAuth();
  const isDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);
  const [startTime, setStartTime] = useState<string>(
    config.burnInRefresh?.reported?.time ?? '--:--',
  );
  const { t } = useTranslation();
  const toast = useToast();
  const [updateBurnInRefreshTime] = useUpdateConfigBurnInRefreshTimeMutation();

  useEffect(() => {
    setStartTime(config.burnInRefresh?.reported?.time ?? '--:--');
  }, [config.burnInRefresh?.reported?.time]);

  const handleUpdateTime = useCallback(
    async (time: string) => {
      if (!HOUR_REGEX.test(time)) return;
      onPendingChange(true);
      try {
        await updateBurnInRefreshTime({
          variables: {
            input: {
              configurationId: config.id,
              time,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('burnInRefreshErr'),
          description: fromError(err, 'UpdateConfigBurnInRefresh'),
        });
      } finally {
        onPendingChange(false);
      }
    },
    [config, toast, updateBurnInRefreshTime, t, onPendingChange],
  );

  const updateTimeValue = (_property: 'start' | 'end', value: string) => {
    if (!HOUR_REGEX.test(value)) return;
    setStartTime(value);
    handleUpdateTime(value);
  };

  return (
    <FormControl>
      <FormLabel>{t('startTime')}</FormLabel>
      <Box maxWidth="80px">
        <TimeSelect
          value={startTime}
          onChange={updateTimeValue}
          property="start"
          disabled={isDisabled}
        />
      </Box>
    </FormControl>
  );
}

ConfigBurnInRefreshTime.graphql = {
  fragments: {
    ConfigBurnInRefreshTime_config: gql`
      fragment ConfigBurnInRefreshTime_config on ConfigurationModel {
        id
        burnInRefresh {
          reported {
            time
          }
        }
      }
    `,
  },
  mutations: {
    UpdateConfigBurnInRefreshTime: gql`
      mutation updateConfigBurnInRefreshTime($input: ConfigurationUpdateBurnInRefreshInput!) {
        configurationUpdateBurnInRefresh(input: $input) {
          id
        }
      }
    `,
  },
};
